import React from "react";
import Icon from "./Icon";

/**
 * Indicium — the canonical "small framed glyph" primitive.
 *
 * In editorial / heraldic typography, an *indicium* is a small
 * identifying symbol set inside an ornamental frame (think: the little
 * wax-stamp you see at the top of letterhead, or the section motifs in
 * a heritage gazette). On the platform it's used everywhere a Phosphor
 * icon would otherwise float bare on the page — Dashboard stat tiles,
 * section headers, AdminConsole tool tiles, persona pickers.
 *
 * The frame is the platform's ornamental container:
 *   - cream "paper" background (or umber on dark surfaces)
 *   - 1px gold hairline ring
 *   - subtle letterpress inset highlight + shadow so the icon reads as
 *     pressed into paper, not floating on top
 *   - rounded-square (default) or circular shape
 *
 * The point: every framed glyph on the platform comes from this one
 * primitive so the brand reads as a single coherent stamp-and-seal
 * aesthetic. The WaxSeal stays the hero; everything else is its
 * understated cousin.
 *
 * Usage:
 *   <Indicium icon={Dog} />                 // 36px square, ink, light
 *   <Indicium icon={Sparkle} tone="gold" /> // gold-tinted frame
 *   <Indicium icon={UsersFour} shape="circle" size="lg" />
 *   <Indicium icon={Crown} variant="dark" /> // for use on ink panels
 */

// Frame size → icon size mapping. Tuned so the icon takes ~50% of the
// frame width — the editorial-luxury sweet spot. Tighter than that and
// the icon dominates; looser and the frame feels empty.
const FRAME_SCALE = {
  sm: { frame: 28, iconToken: "xs" }, // 14px icon
  md: { frame: 36, iconToken: "md" }, // 18px icon
  lg: { frame: 44, iconToken: "lg" }, // 22px icon
};

const TONE_FRAMES = {
  // Default — cream paper with gold hairline ring + letterpress shadow.
  ink: {
    light:
      "bg-cream ring-1 ring-[#E2DAC2] shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_1px_2px_rgba(0,0,0,0.04)]",
    dark:
      "bg-umber ring-1 ring-gold/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.4)]",
  },
  // Gold tone — slightly warmer cream + the actual gold ring + a hint
  // of gold glow underneath. The "WaxSeal-lite" treatment for premium
  // signals (verified kennels, featured listings) where the full
  // medallion would be too loud.
  gold: {
    light:
      "bg-[#FBF1D8] ring-1 ring-gold/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_8px_rgba(201,162,39,0.18)]",
    dark:
      "bg-umber ring-1 ring-gold/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_10px_rgba(201,162,39,0.2)]",
  },
  // Crimson tone — for urgent / destructive / female-dog signals where
  // the standard ink frame would underplay the urgency.
  crimson: {
    light:
      "bg-[#FBEEEE] ring-1 ring-crimson/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_1px_3px_rgba(193,39,45,0.12)]",
    dark:
      "bg-umber ring-1 ring-crimson/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_3px_rgba(0,0,0,0.4)]",
  },
};

// What colour does the icon itself take inside each frame? Picked so
// the icon and frame read as one coherent stamp instead of two layers.
const ICON_TONE = {
  ink:     { light: "ink",     dark: "white" },
  gold:    { light: "gold",    dark: "gold" },
  crimson: { light: "crimson", dark: "crimson" },
};

export const Indicium = ({
  icon,
  tone = "ink",
  size = "md",
  shape = "rounded",
  variant = "light",
  className = "",
  ...rest
}) => {
  const frameSpec = FRAME_SCALE[size] || FRAME_SCALE.md;
  const toneFrame = (TONE_FRAMES[tone] || TONE_FRAMES.ink)[variant] || TONE_FRAMES.ink.light;
  const iconToneName = (ICON_TONE[tone] || ICON_TONE.ink)[variant] || "ink";
  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-lg";

  return (
    <span
      data-testid="indicium"
      data-tone={tone}
      data-variant={variant}
      className={`inline-flex items-center justify-center flex-shrink-0 ${shapeClass} ${toneFrame} ${className}`}
      style={{ width: frameSpec.frame, height: frameSpec.frame }}
      {...rest}
    >
      <Icon as={icon} tone={iconToneName} size={frameSpec.iconToken} />
    </span>
  );
};

export default Indicium;
